import { create } from 'zustand';
import {
  emitNotificationRead,
  emitAllNotificationsRead,
  emitNotificationsCleared,
  NotificationIdentifier,
} from '../lib/websocket';
import {
  getNotifications,
  markAsRead as markAsReadApi,
  markAllAsRead as markAllAsReadApi,
  clearAllNotifications as clearAllApi,
  Notification as ServerNotification,
  NotificationType as ServerNotificationType,
} from '../lib/notifications';

export type NotificationType =
  | 'site_assigned'
  | 'site_unassigned'
  | 'site_updated'
  | 'site_primary_needed'
  | 'site_assignment_changed'
  | 'site_agency_linked'            // 대행 기관 연결 (P4)
  | 'site_agency_unlinked'          // 대행 연결 해제·종료 (P4)
  | 'site_agency_link_requested'    // 대행 연결 요청 (P4 — 업체 소유자가 처리)
  | 'site_agency_link_resolved'     // 대행 연결 요청 승인·거절 (P4 — 기관이 받는다)
  | 'member_invited'
  | 'member_joined'
  | 'member_left'
  | 'member_role_changed'
  | 'organization_restored'
  | 'company_merge_request'         // 조직 통합 요청 받음 (대상 조직 OWNER)
  | 'company_merge_accepted'        // 보낸 통합 요청이 수락됨 (요청 조직 OWNER)
  | 'company_merge_rejected'        // 보낸 통합 요청이 거절됨 (요청 조직 OWNER)
  | 'inspection_created'
  | 'inspection_contributor_joined_room'
  | 'inspection_all_reviews_done'
  | 'inspection_modified'           // 점검 자료 수정됨
  | 'inspection_contributor_joined' // 새 참여자 합류
  | 'inspection_soft_deleted'       // 점검 삭제됨
  | 'inspection_restored'           // 점검 복원됨
  | 'inspection_completed'          // 점검 완료됨
  | 'inspection_review_requested'   // 검토 요청
  | 'report_created'                // 보고서 생성됨
  | 'report_deleted'                // 보고서 삭제됨
  | 'report_submitted'              // 보고서 결재 제출됨
  | 'report_submission_cancelled'   // 보고서 제출 취소됨
  | 'invitation_accepted'
  | 'invitation_rejected'
  | 'invitation_sent'
  | 'invitation_cancelled'
  | 'ai_analysis_started'           // AI 분석 시작
  | 'ai_analysis_completed'         // AI 분석 완료
  | 'ai_analysis_failed'            // AI 분석 실패
  | 'pdf_generated'                 // PDF 생성 완료
  | 'verification_completed'        // 기관 인증 완료
  | 'verification_mismatch'         // 기관 인증 정보 불일치 — 정정 필요
  | 'phone_removed'                 // 다른 계정이 같은 번호를 인증해 내 번호가 해제됨
  | 'legal_obligation_due_soon'     // 법정 서류 의무의 기한 임박 (줄기 항목 3)
  | 'legal_obligation_event_revoked' // 법정 서류 사건 등록 취소 (9-o)
  | 'legal_obligation_agency_closing' // 법정 서류 대행 기관 탈퇴 예정 (L32 결정 ①)
  | 'action_success'
  | 'info'
  | 'dlr_draft_completed';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  createdAt: number; // timestamp
  data?: Record<string, unknown>; // 추가 데이터 (링크용 등)
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isFetching: boolean;
  lastFetchedAt: number | null;
  ownerUserId: string | null; // 알림 데이터 소유자 userId
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  // 다른 세션에서 동기화된 이벤트 처리 (WebSocket)
  syncMarkAsRead: (identifier: NotificationIdentifier) => void;
  syncMarkAllAsRead: () => void;
  syncClearAll: () => void;
  // 서버에서 알림 목록 가져오기 (WebSocket 연결 시 동기화)
  fetchFromServer: () => Promise<void>;
  // 사용자 변경 시 알림 초기화 (로그인 시 호출)
  initializeForUser: (userId: string) => void;
}

const MAX_NOTIFICATIONS = 50; // 최대 저장 개수
const TIME_TOLERANCE_MS = 5000; // 알림 매칭 시 시간 허용 오차 (5초)
const FETCH_COOLDOWN_MS = 30000; // 서버 동기화 쿨다운 (30초)

/**
 * 알림에서 동기화용 식별자 생성
 */
const createNotificationIdentifier = (notification: Notification): NotificationIdentifier => {
  // data에서 핵심 식별 키 추출 (companyId, siteId, userId 등)
  const dataKey = notification.data
    ? JSON.stringify({
        companyId: notification.data.companyId,
        siteId: notification.data.siteId,
        userId: notification.data.userId,
        inspectionId: notification.data.inspectionId,
      })
    : undefined;

  return {
    type: notification.type,
    createdAt: notification.createdAt,
    dataKey,
  };
};

/**
 * 식별자로 알림 찾기 (시간 허용 오차 적용)
 */
const findNotificationByIdentifier = (
  notifications: Notification[],
  identifier: NotificationIdentifier
): Notification | undefined => {
  return notifications.find((n) => {
    // 타입 매칭
    if (n.type !== identifier.type) return false;

    // 시간 매칭 (허용 오차 적용)
    const timeDiff = Math.abs(n.createdAt - identifier.createdAt);
    if (timeDiff > TIME_TOLERANCE_MS) return false;

    // dataKey 매칭 (있는 경우)
    if (identifier.dataKey) {
      const nDataKey = n.data
        ? JSON.stringify({
            companyId: n.data.companyId,
            siteId: n.data.siteId,
            userId: n.data.userId,
            inspectionId: n.data.inspectionId,
          })
        : undefined;
      if (nDataKey !== identifier.dataKey) return false;
    }

    return true;
  });
};

/**
 * 서버 알림 타입을 로컬 알림 타입으로 변환
 */
export const mapServerTypeToLocalType = (serverType: ServerNotificationType | string): NotificationType => {
  const typeMap: Record<string, NotificationType> = {
    // 보고서 관련
    REPORT_SIGNATURE_REQUEST: 'report_created',
    REPORT_SIGNATURE_REMINDER: 'report_created',
    REPORT_ALL_SIGNED: 'report_created',
    REPORT_CREATED: 'report_created',
    REPORT_DELETED: 'report_deleted',
    REPORT_SUBMITTED: 'report_submitted',
    REPORT_SUBMISSION_CANCELLED: 'report_submission_cancelled',
    // 결재 관련
    APPROVAL_REQUEST: 'info',
    APPROVAL_COMPLETED: 'info',
    APPROVAL_REJECTED: 'info',
    // 멤버/조직 관련
    MEMBER_INVITED: 'member_invited',
    MEMBER_JOINED: 'member_joined',
    MEMBER_LEFT: 'member_left',
    MEMBER_ROLE_CHANGED: 'member_role_changed',
    ORGANIZATION_RESTORED: 'organization_restored',
    // 조직 통합(테넌트 흡수) 관련 — 셋 다 resourceType은 company_merge_request로 같고
    // resourceId도 같은 토큰이라, **어디로 보낼지는 이 타입으로만 갈린다**(수신 조직은 수락
    // 화면으로, 요청 조직은 진행 상태를 보는 조직 설정으로). 'info'로 뭉개면 그 구분이 사라진다.
    COMPANY_MERGE_REQUEST: 'company_merge_request',
    COMPANY_MERGE_ACCEPTED: 'company_merge_accepted',
    COMPANY_MERGE_REJECTED: 'company_merge_rejected',
    // 현장 관련
    SITE_ASSIGNED: 'site_assigned',
    SITE_UNASSIGNED: 'site_unassigned',
    SITE_ASSIGNMENT_CHANGED: 'site_assignment_changed',
    SITE_UPDATED: 'site_updated',
    SITE_PRIMARY_NEEDED: 'site_primary_needed',
    // 대행 현장 연결(P4) — ⛔ 빠지면 아래 폴백이 `'info'`로 조용히 접는다
    SITE_AGENCY_LINKED: 'site_agency_linked',
    SITE_AGENCY_UNLINKED: 'site_agency_unlinked',
    SITE_AGENCY_LINK_REQUESTED: 'site_agency_link_requested',
    SITE_AGENCY_LINK_RESOLVED: 'site_agency_link_resolved',
    // 점검 관련
    INSPECTION_CREATED: 'inspection_created',
    INSPECTION_COMPLETED: 'inspection_completed',
    INSPECTION_MODIFIED: 'inspection_modified',
    INSPECTION_DELETED: 'inspection_soft_deleted',
    INSPECTION_RESTORED: 'inspection_restored',
    INSPECTION_CONTRIBUTOR_JOINED: 'inspection_contributor_joined',
    INSPECTION_REVIEW_REQUESTED: 'inspection_review_requested',
    // 초대 관련
    INVITATION_CANCELLED: 'invitation_cancelled',
    INVITATION_REJECTED: 'invitation_rejected',
    // AI 분석 관련
    AI_ANALYSIS_STARTED: 'ai_analysis_started',
    AI_ANALYSIS_COMPLETED: 'ai_analysis_completed',
    AI_ANALYSIS_FAILED: 'ai_analysis_failed',
    // PDF 관련
    PDF_GENERATED: 'pdf_generated',
    // DLR 위임 관련
    DLR_DRAFT_COMPLETED: 'dlr_draft_completed',
    // 계정/신원 관련
    PHONE_REMOVED: 'phone_removed',
    // 법정 서류 관리 (줄기 항목 3) — ⛔ 이 줄이 빠지면 아래 폴백이 `'info'`로 **조용히** 접어
    // 일반 아이콘이 뜬다(red 가 없다). `NotificationType`에 값을 더하는 커밋이 여기도 본다.
    LEGAL_OBLIGATION_DUE_SOON: 'legal_obligation_due_soon',
    LEGAL_OBLIGATION_EVENT_REVOKED: 'legal_obligation_event_revoked',
    LEGAL_OBLIGATION_AGENCY_CLOSING: 'legal_obligation_agency_closing',
  };
  return typeMap[serverType] || 'info';
};

/**
 * 서버 알림을 로컬 알림 형식으로 변환
 */
const convertServerNotification = (serverNotification: ServerNotification): Notification => {
  return {
    id: serverNotification.id,
    type: mapServerTypeToLocalType(serverNotification.type),
    title: serverNotification.title,
    message: serverNotification.message,
    read: serverNotification.isRead,
    createdAt: new Date(serverNotification.createdAt).getTime(),
    data: {
      resourceType: serverNotification.resourceType,
      resourceId: serverNotification.resourceId,
      /**
       * **서버가 정한 목적지**(2026-09-08). 종전엔 이 변환에서 통째로 버려져, store를 읽는
       * 벨 드롭다운은 `actionUrl`을 **원리적으로 볼 수 없었다** — 그래서 목적지를 자기
       * switch로 다시 계산했고, 거기 없는 타입은 눌러도 아무 일이 없었다.
       *
       * 전체 알림 페이지(`pages/notifications`)는 API 응답을 그대로 써서 `actionUrl`을
       * 우선하므로, 같은 알림이 **두 인앱 surface에서 다르게 동작**했다(2026-09-08 dev
       * 실측: 「내가 직접 진행」 TBM 교육 리마인더가 전체 페이지에선 교육 화면으로 갔고
       * 벨에선 죽었다). 값을 옮겨 두 화면이 같은 재료를 보게 한다.
       */
      actionUrl: serverNotification.actionUrl,
      companyId: serverNotification.companyId,
      serverId: serverNotification.id, // 서버 ID 보존 (중복 방지용)
    },
  };
};

export const useNotificationStore = create<NotificationState>()((set, get) => ({
      notifications: [],
      unreadCount: 0,
      isFetching: false,
      lastFetchedAt: null,
      ownerUserId: null,

      addNotification: (notification) => {
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newNotification: Notification = {
          ...notification,
          id,
          read: false,
          createdAt: Date.now(),
        };

        set((state) => {
          // 최대 개수 초과 시 오래된 것 제거
          const notifications = [newNotification, ...state.notifications].slice(0, MAX_NOTIFICATIONS);
          const unreadCount = notifications.filter((n) => !n.read).length;
          return { notifications, unreadCount };
        });
      },

      markAsRead: (id) => {
        const notification = get().notifications.find((n) => n.id === id);
        if (!notification || notification.read) return;

        // 로컬 상태 즉시 업데이트 (낙관적 업데이트)
        set((state) => {
          const notifications = state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          );
          const unreadCount = notifications.filter((n) => !n.read).length;
          return { notifications, unreadCount };
        });

        // 다른 세션에 동기화 (WebSocket)
        const identifier = createNotificationIdentifier(notification);
        emitNotificationRead(identifier);

        // 서버 API 호출 (서버 ID가 있는 경우)
        const serverId = notification.data?.serverId as string | undefined;
        if (serverId) {
          markAsReadApi(serverId).catch((error) => {
            console.error('[Notification] Failed to mark as read on server:', error);
          });
        }
      },

      markAllAsRead: () => {
        const hasUnread = get().unreadCount > 0;
        if (!hasUnread) return;

        // 로컬 상태 즉시 업데이트 (낙관적 업데이트)
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));

        // 다른 세션에 동기화 (WebSocket)
        emitAllNotificationsRead();

        // 서버 API 호출
        markAllAsReadApi().catch((error) => {
          console.error('[Notification] Failed to mark all as read on server:', error);
        });
      },

      removeNotification: (id) => {
        set((state) => {
          const notifications = state.notifications.filter((n) => n.id !== id);
          const unreadCount = notifications.filter((n) => !n.read).length;
          return { notifications, unreadCount };
        });
      },

      clearAll: () => {
        const hasNotifications = get().notifications.length > 0;
        if (!hasNotifications) return;

        // 로컬 상태 즉시 업데이트 (낙관적 업데이트)
        set({ notifications: [], unreadCount: 0 });

        // 다른 세션에 동기화 (WebSocket)
        emitNotificationsCleared();

        // 서버 API 호출 (알림 삭제)
        clearAllApi().catch((error) => {
          console.error('[Notification] Failed to clear all on server:', error);
        });
      },

      // ============================================
      // 다른 세션에서 동기화된 이벤트 처리
      // (WebSocket에서 수신, 로컬에만 반영하고 재전파하지 않음)
      // ============================================

      syncMarkAsRead: (identifier: NotificationIdentifier) => {
        set((state) => {
          const targetNotification = findNotificationByIdentifier(state.notifications, identifier);
          if (!targetNotification || targetNotification.read) {
            return state; // 이미 읽음 상태거나 찾을 수 없음
          }

          const notifications = state.notifications.map((n) =>
            n.id === targetNotification.id ? { ...n, read: true } : n
          );
          const unreadCount = notifications.filter((n) => !n.read).length;
          return { notifications, unreadCount };
        });
      },

      syncMarkAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      syncClearAll: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      // 사용자 변경 시 알림 초기화 (로그인 시 호출)
      // 서버에서 새로 가져오므로 항상 초기화
      initializeForUser: (userId: string) => {
        console.log(`[Notification] Initializing for user: ${userId}`);
        set({
          notifications: [],
          unreadCount: 0,
          ownerUserId: userId,
          lastFetchedAt: null,
        });
      },

      // 서버에서 알림 목록 가져와서 로컬 상태와 병합
      fetchFromServer: async () => {
        const { isFetching, lastFetchedAt } = get();

        // 이미 가져오는 중이면 스킵
        if (isFetching) {
          console.log('[Notification] Already fetching, skip');
          return;
        }

        // 쿨다운 체크 (30초 이내 재요청 방지)
        if (lastFetchedAt && Date.now() - lastFetchedAt < FETCH_COOLDOWN_MS) {
          console.log('[Notification] Fetch cooldown, skip');
          return;
        }

        set({ isFetching: true });

        try {
          console.log('[Notification] Fetching notifications from server...');
          const response = await getNotifications({ limit: MAX_NOTIFICATIONS });
          const serverNotifications = response.data;

          // 서버 알림을 로컬 형식으로 변환
          const convertedNotifications = serverNotifications.map(convertServerNotification);

          set((state) => {
            // 로컬 전용 알림만 유지 (action_success 타입만)
            // - serverId가 있는 알림: 서버에서 관리되므로 서버 목록 기준
            // - serverId가 없지만 action_success가 아닌 알림: 예전에 잘못 저장된 것이므로 제거
            const localOnlyNotifications = state.notifications.filter((n) => {
              const serverId = n.data?.serverId as string | undefined;
              // serverId가 있으면 서버 알림이므로 서버 목록에서 관리
              if (serverId) return false;
              // serverId가 없으면 action_success 타입만 로컬 전용으로 유지
              return n.type === 'action_success';
            });

            // 서버 알림 + 로컬 전용 알림(action_success만) 병합
            const merged = [...convertedNotifications, ...localOnlyNotifications];

            // 시간순 정렬 (최신순)
            merged.sort((a, b) => b.createdAt - a.createdAt);

            // 최대 개수 제한
            const notifications = merged.slice(0, MAX_NOTIFICATIONS);
            const unreadCount = notifications.filter((n) => !n.read).length;

            console.log(`[Notification] Synced: ${serverNotifications.length} from server, ${localOnlyNotifications.length} local-only (action_success), ${notifications.length} total`);

            return {
              notifications,
              unreadCount,
              isFetching: false,
              lastFetchedAt: Date.now(),
            };
          });
        } catch (error) {
          console.error('[Notification] Failed to fetch from server:', error);
          set({ isFetching: false });
        }
      },
}));
